import React, { Fragment, useEffect, useState } from 'react';
import Footer from '../Footer/Footer';
import Loader from '../../loader/Loader';
import PageSEO from '../Common/PageSEO';
import {
    FeatureHero,
    FeatureHeroCta,
    FeatureChain,
    FeatureSection,
    FeatureDownloadCta
} from '../Common/FeatureContent';
import { useLocale } from '../../localization/LocaleContext';
import { useCurrency } from '../../localization/CurrencyContext';
import { ROUTE_PATHS } from '../../localization/constants';
import './QuotesMain.css';

const PILLAR_ICONS = [
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="17" y1="10" x2="3" y2="10"/>
            <line x1="21" y1="6" x2="3" y2="6"/>
            <line x1="21" y1="14" x2="3" y2="14"/>
            <line x1="17" y1="18" x2="3" y2="18"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.59 13.41 11 3.83 3.83 11 13.41 20.59a2 2 0 0 0 2.83 0l4.35-4.35a2 2 0 0 0 0-2.83z"/>
            <circle cx="7.5" cy="7.5" r="1.5"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="2" width="16" height="20" rx="2"/>
            <line x1="8" y1="7" x2="16" y2="7"/>
            <line x1="8" y1="11" x2="16" y2="11"/>
            <line x1="8" y1="15" x2="12" y2="15"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="1" x2="12" y2="23"/>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.8 17 5 19 5a1 1 0 0 1 1 1z"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/>
            <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/>
            <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/>
            <line x1="10" y1="6" x2="14" y2="6"/>
            <line x1="10" y1="10" x2="14" y2="10"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M13 2 3 14h9l-1 8 10-12h-9z"/>
        </svg>
    )
];

const DEFAULT_STATE = {
    client: '',
    description: '',
    labor: 1150,
    materials: 480,
    validity: '30',
    tax: 21
};

function QuotesMain(){
    const { t, locale } = useLocale();
    const { formatCurrency } = useCurrency();
    const p = (key) => t(`featurePages.quotes.${key}`);
    const defaultForm = {
        ...DEFAULT_STATE,
        client: p('defaultClientName'),
        description: p('defaultDescription')
    };

    const [loader, setLoader] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [converted, setConverted] = useState(false);
    const [converting, setConverting] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setLoader(true);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    if (!loader) {
        return <Loader/>;
    }

    const handleChange = (field) => (e) => {
        const value = e.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleConvert = () => {
        setConverting(true);
        setTimeout(() => {
            setConverting(false);
            setConverted(true);
        }, 600);
    };

    const handleReset = () => {
        setForm(defaultForm);
        setConverted(false);
        setConverting(false);
    };

    const labor = parseFloat(form.labor) || 0;
    const materials = parseFloat(form.materials) || 0;
    const taxRate = parseFloat(form.tax) || 0;
    const subtotal = labor + materials;
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;

    const pillars = [1, 2, 3, 4, 5, 6, 7, 8].map((num) => ({
        icon: PILLAR_ICONS[num - 1],
        title: p(`pillar${num}Title`),
        description: p(`pillar${num}Description`)
    }));

    const workflowSteps = [1, 2, 3].map((num) => ({
        title: p(`step${num}Title`),
        description: p(`step${num}Description`)
    }));

    return(
        <Fragment>
            <PageSEO title={p('metaTitle')} description={p('metaDescription')} path={ROUTE_PATHS.quotes} locale={locale} />

            <FeatureHero
                eyebrow={p('eyebrow')}
                title={p('heroTitle')}
                highlight={p('heroHighlight')}
                subtitle={p('heroSubtitle')}
                cta={
                    <FeatureHeroCta
                        primaryLabel={t('shared.featureStartFree')}
                        primaryTarget="feature-download"
                        secondaryLabel={t('shared.featureSeeHowItWorks')}
                        secondaryTarget="feature-content"
                    />
                }
            />

            {/* Interactive Quote Builder & Preview */}
            <section id="feature-content" className="qt-builder-section">
                <div className="container">
                    <div className="fc-section-header">
                        <h2 className="h-1 h-bold fc-section-title">{p('builderSectionTitle')}</h2>
                        <p className="fc-section-description">{p('builderSectionDescription')}</p>
                    </div>

                    <div className="qt-builder-grid">
                        {/* Left: Controls */}
                        <div className="qt-panel">
                            <div className="qt-panel-header">
                                <h3 className="qt-panel-title">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="4" y1="21" x2="4" y2="14"/>
                                        <line x1="4" y1="10" x2="4" y2="3"/>
                                        <line x1="12" y1="21" x2="12" y2="12"/>
                                        <line x1="12" y1="8" x2="12" y2="3"/>
                                        <line x1="20" y1="21" x2="20" y2="16"/>
                                        <line x1="20" y1="12" x2="20" y2="3"/>
                                        <line x1="1" y1="14" x2="7" y2="14"/>
                                        <line x1="9" y1="8" x2="15" y2="8"/>
                                        <line x1="17" y1="16" x2="23" y2="16"/>
                                    </svg>
                                    <span>{p('panelTitle')}</span>
                                </h3>
                                <span className={`qt-status-badge${converted ? ' converted' : ''}`}>
                                    {converted ? p('statusConverted') : p('statusDraft')}
                                </span>
                            </div>

                            <div className="qt-field-group">
                                <label className="qt-field-label">{p('labelClient')}</label>
                                <input
                                    type="text"
                                    className="qt-input"
                                    value={form.client}
                                    onChange={handleChange('client')}
                                />
                            </div>

                            <div className="qt-field-group">
                                <label className="qt-field-label">{p('labelDesc')}</label>
                                <input
                                    type="text"
                                    className="qt-input"
                                    value={form.description}
                                    onChange={handleChange('description')}
                                />
                            </div>

                            <div className="qt-field-group qt-field-row">
                                <div>
                                    <label className="qt-field-label">{p('labelLabor')}</label>
                                    <input
                                        type="number"
                                        className="qt-input"
                                        value={form.labor}
                                        onChange={handleChange('labor')}
                                    />
                                </div>
                                <div>
                                    <label className="qt-field-label">{p('labelMaterials')}</label>
                                    <input
                                        type="number"
                                        className="qt-input"
                                        value={form.materials}
                                        onChange={handleChange('materials')}
                                    />
                                </div>
                            </div>

                            <div className="qt-field-group qt-field-row">
                                <div>
                                    <label className="qt-field-label">{p('labelValidity')}</label>
                                    <select
                                        className="qt-select"
                                        value={form.validity}
                                        onChange={handleChange('validity')}
                                    >
                                        <option value="14">{p('validity14')}</option>
                                        <option value="30">{p('validity30')}</option>
                                        <option value="60">{p('validity60')}</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="qt-field-label">{p('labelTax')}</label>
                                    <input
                                        type="number"
                                        className="qt-input"
                                        value={form.tax}
                                        onChange={handleChange('tax')}
                                    />
                                </div>
                            </div>

                            <div className="qt-panel-actions">
                                <button type="button" className="qt-btn-convert" onClick={handleConvert}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                        <polyline points="14 2 14 8 20 8"/>
                                        <line x1="16" y1="13" x2="8" y2="13"/>
                                        <line x1="16" y1="17" x2="8" y2="17"/>
                                    </svg>
                                    <span>
                                        {converting ? p('convertingButton') : converted ? p('convertedButton') : p('convertButton')}
                                    </span>
                                </button>
                                <button type="button" className="qt-btn-reset" onClick={handleReset}>
                                    {p('resetButton')}
                                </button>
                            </div>
                        </div>

                        {/* Right: Live Preview */}
                        <div className="qt-preview-card">
                            <div className="qt-preview-header">
                                <div className="qt-preview-brand">
                                    <div className="qt-preview-brand-icon">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                            <polyline points="14 2 14 8 20 8"/>
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="qt-preview-brand-name">{p('previewBrandName')}</div>
                                        <div className="qt-preview-brand-meta">{p('previewBrandAddress')}</div>
                                    </div>
                                </div>
                                <div className="qt-preview-tag">
                                    <span className="qt-preview-tag-label">{p('previewTag')}</span>
                                    <div className="qt-preview-tag-number">{p('previewQuoteNo')}</div>
                                </div>
                            </div>

                            <div className="qt-preview-meta-row">
                                <div>
                                    <span className="qt-preview-meta-label">{p('billToLabel')}</span>
                                    <span className="qt-preview-meta-value">{form.client || p('defaultClientName')}</span>
                                </div>
                                <div>
                                    <span className="qt-preview-meta-label">{p('validUntilLabel')}</span>
                                        <span className="qt-preview-meta-value">{form.validity} {p('daysLabel')}</span>
                                </div>
                            </div>

                            <table className="qt-preview-table">
                                <thead>
                                    <tr>
                                        <th>{p('tableDescriptionHeader')}</th>
                                        <th className="qt-col-center">{p('tableQtyHeader')}</th>
                                        <th className="qt-col-right">{p('tableRateHeader')}</th>
                                        <th className="qt-col-right">{p('tableTotalHeader')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{form.description || p('defaultDescription')}</td>
                                        <td className="qt-col-center">1</td>
                                        <td className="qt-col-right">{formatCurrency(labor)}</td>
                                        <td className="qt-col-right">{formatCurrency(labor)}</td>
                                    </tr>
                                    <tr>
                                        <td>{p('materialsLineLabel')}</td>
                                        <td className="qt-col-center">1</td>
                                        <td className="qt-col-right">{formatCurrency(materials)}</td>
                                        <td className="qt-col-right">{formatCurrency(materials)}</td>
                                    </tr>
                                </tbody>
                            </table>

                            <div className="qt-totals">
                                <div className="qt-totals-row">
                                    <span>{p('subtotalLabel')}</span>
                                    <span>{formatCurrency(subtotal)}</span>
                                </div>
                                <div className="qt-totals-row">
                                    <span>{p('taxLabel')} ({taxRate}%)</span>
                                    <span>{formatCurrency(taxAmount)}</span>
                                </div>
                                <div className="qt-totals-row qt-total-final">
                                    <span>{p('totalLabel')}</span>
                                    <span className="qt-total-final-value">{formatCurrency(total)}</span>
                                </div>
                            </div>

                            {converted && (
                                <div className="qt-success-banner">
                                    <svg className="qt-success-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                        <polyline points="22 4 12 14.01 9 11.01"/>
                                    </svg>
                                    <div>
                                        <p className="qt-success-title">{p('successTitle')}</p>
                                        <p className="qt-success-subtitle">{p('successSubtitle')}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Feature Pillars Grid */}
            <section className="qt-pillars-section">
                <div className="container">
                    <div className="fc-section-header">
                        <h2 className="h-1 h-bold fc-section-title">{p('pillarsSectionTitle')}</h2>
                        <p className="fc-section-description">{p('pillarsSectionDescription')}</p>
                    </div>
                    <div className="qt-pillars-grid">
                        {pillars.map((pillar, idx) => (
                            <div className="qt-pillar-card" key={idx}>
                                <div className="qt-pillar-icon-box">{pillar.icon}</div>
                                <h3 className="qt-pillar-title">{pillar.title}</h3>
                                <p className="qt-pillar-desc">{pillar.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Workflow Section */}
            <FeatureSection
                alt
                title={p('workflowSectionTitle')}
                description={p('workflowSectionDescription')}
            >
                <FeatureChain steps={[p('step1Title'), p('step2Title'), p('step3Title')]} />

                <div className="qt-steps-grid">
                    {workflowSteps.map((step, idx) => (
                        <div className="qt-step-card" key={idx}>
                            <div className="qt-step-number">{`0${idx + 1}`}</div>
                            <h3 className="qt-step-title">{step.title}</h3>
                            <p className="qt-step-description">{step.description}</p>
                        </div>
                    ))}
                </div>
            </FeatureSection>

            <FeatureDownloadCta
                title={p('ctaTitle')}
                description={p('ctaDescription')}
                ctaLabel={p('ctaButton')}
                storesLabel={t('shared.featureDownloadApp')}
            />

            <Footer />
        </Fragment>
    )
}

export default QuotesMain;